import { PermissionLevel, PermissionsMap } from "./permissions";
import { useUserStore } from "@/stores/userStore";

/**
 * Get the permission level for a specific permission key from user's permissions
 * @param permissions - User's permissions map
 * @param permissionKey - The permission key to check (e.g., 'clientManagement', 'projectManagement')
 * @returns The permission level ('no', 'read', or 'full') or 'no' if not found
 */
export function getPermissionLevel(
  permissions: PermissionsMap | undefined,
  permissionKey: keyof PermissionsMap,
): PermissionLevel {
  if (!permissions) return "no";
  return (permissions[permissionKey] ?? "no") as PermissionLevel;
}

/**
 * Check if user has at least the specified permission level
 * @param permissions - User's permissions map
 * @param permissionKey - The permission key to check
 * @param requiredLevel - The minimum required permission level
 * @returns true if user has at least the required permission level
 */
export function hasPermission(
  permissions: PermissionsMap | undefined,
  permissionKey: keyof PermissionsMap,
  requiredLevel: PermissionLevel,
): boolean {
  const level = getPermissionLevel(permissions, permissionKey);

  if (requiredLevel === "no") return true;
  if (requiredLevel === "read") return level === "read" || level === "full";
  if (requiredLevel === "full") return level === "full";

  return false;
}

/**
 * Check if user can view (has read or full access)
 * @param permissions - User's permissions map
 * @param permissionKey - The permission key to check
 * @returns true if user has read or full access
 */
export function canView(permissions: PermissionsMap | undefined, permissionKey: keyof PermissionsMap): boolean {
  return hasPermission(permissions, permissionKey, "read");
}

/**
 * Check if user can edit (has full access)
 * @param permissions - User's permissions map
 * @param permissionKey - The permission key to check
 * @returns true if user has full access
 */
export function canEdit(permissions: PermissionsMap | undefined, permissionKey: keyof PermissionsMap): boolean {
  return hasPermission(permissions, permissionKey, "full");
}

/**
 * Custom hook to access user permissions and permission checking functions
 * @returns Object with permissions and helper functions
 */
export function usePermissions() {
  const user = useUserStore((state) => state.user);
  const permissions = user?.permissions as PermissionsMap | undefined;

  return {
    permissions,
    canView: (permissionKey: keyof PermissionsMap) => canView(permissions, permissionKey),
    canEdit: (permissionKey: keyof PermissionsMap) => canEdit(permissions, permissionKey),
    getPermissionLevel: (permissionKey: keyof PermissionsMap) => getPermissionLevel(permissions, permissionKey),
  };
}
